import * as React from 'react';
import { Grid, GridCellProps, GridColumn as Column, GridColumnReorderEvent, GridColumnResizeEvent, GridFilterChangeEvent, GridSortChangeEvent, GridToolbar } from "@progress/kendo-react-grid";
import "./AsolviGrid.css";
import { CompositeFilterDescriptor, filterBy, orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { ExcelExport, ExcelExportColumn } from '@progress/kendo-react-excel-export';
import AsolviButton from '../AsolviButton/AsolviButton';
import { Col, Container, Row } from 'react-bootstrap';
import * as ReactI18Next from 'react-i18next';
import * as I18Next from 'i18next';
import { CSSProperties } from "react";

export interface IGridLayout{
  fieldName: string,
  fieldLanguageKey?: string,
  colWidth?: number,
  editable: boolean,
  visible: boolean,
  editor?: "boolean" | "text" | "numeric" | "date" | undefined,
  format?: string | undefined,
  filterType?: "date" | "numeric" | "text" | "boolean" | undefined,
  cellStyle?: any,
  initialSort?: "asc" | "desc" | undefined
}

export interface IProps extends ReactI18Next.WithTranslation {
  t: I18Next.TFunction;
  data: any[],
  dataKeyField: string,
  gridLayout?: IGridLayout[] | undefined,
  handleChange: (e:any) => void,
  sortable?: boolean,
  filterable?: boolean,
  enableExcelExport?: boolean,
  handleRowClick?: (e:any) => void,
  handleItemChange?: (e:any) => void,
  enableAddNew?: boolean,
  handleAddNew?: (e:any) => void,
  enableDelete?: boolean,
  handleDelete?: (e:any) => void,
  gridStyle?: CSSProperties,
  resizable?: boolean,
  reorderable?: boolean,
  storageKey?: string,
  enableColumnChooser?: boolean,
}

const STORAGE_PREFIX = 'asolvi-grid-columns-';

interface IStoredColumn { fieldName: string; colWidth?: number; visible?: boolean; }

function loadColumnsFromStorage(storageKey: string, defaults: IGridLayout[]): IGridLayout[] {
  try {
    const raw = localStorage.getItem(STORAGE_PREFIX + storageKey);
    if (!raw) return defaults;
    const stored: IStoredColumn[] = JSON.parse(raw);
    const merged = stored
      .map(s => defaults.find(d => d.fieldName === s.fieldName))
      .filter((c): c is IGridLayout => c !== undefined)
      .map(col => {
        const s = stored.find(s => s.fieldName === col.fieldName)!;
        return {
          ...col,
          ...(s.colWidth !== undefined ? { colWidth: s.colWidth } : {}),
          ...(s.visible !== undefined ? { visible: s.visible } : {}),
        };
      });
    const storedNames = new Set(stored.map(s => s.fieldName));
    const added = defaults.filter(d => !storedNames.has(d.fieldName));
    return [...merged, ...added];
  } catch {
    return defaults;
  }
}

function saveColumnsToStorage(storageKey: string, cols: IGridLayout[]): void {
  try {
    const data: IStoredColumn[] = cols.map(c => ({ fieldName: c.fieldName, colWidth: c.colWidth, visible: c.visible }));
    localStorage.setItem(STORAGE_PREFIX + storageKey, JSON.stringify(data));
  } catch { /* storage full or unavailable */ }
}

const AsolviGrid: React.FunctionComponent<IProps> = (props) => {
  const [filterState, setFilterState] = React.useState<CompositeFilterDescriptor>({ logic: "and", filters: [] });
  const [columns, setColumns] = React.useState<IGridLayout[]>(() =>
    props.storageKey
      ? loadColumnsFromStorage(props.storageKey, props.gridLayout ?? [])
      : (props.gridLayout ?? [])
  );
  // Tracks current visual column order independently of React render order.
  // We deliberately do NOT re-render on reorder (Kendo owns visual state during
  // the session); this ref lets resize saves use the correct post-reorder order.
  const columnOrderRef = React.useRef<string[]>(columns.map(c => c.fieldName));
  const [showColumnChooser, setShowColumnChooser] = React.useState(false);
  const chooserRef = React.useRef<HTMLDivElement>(null);
  const _export = React.useRef<ExcelExport | null>(null);
  const { t, gridStyle } = props

  React.useEffect(() => {
    if (!showColumnChooser) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (chooserRef.current && !chooserRef.current.contains(e.target as Node)) {
        setShowColumnChooser(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showColumnChooser]);

  // Persist column widths (and order at time of last resize) to storage.
  React.useEffect(() => {
    if (!props.storageKey) return;
    const ordered = columnOrderRef.current
      .map(fn => columns.find(c => c.fieldName === fn))
      .filter((c): c is IGridLayout => c !== undefined);
    const remaining = columns.filter(c => !columnOrderRef.current.includes(c.fieldName));
    saveColumnsToStorage(props.storageKey, [...ordered, ...remaining]);
  }, [columns, props.storageKey]);

  const handleColumnReorder = (e: GridColumnReorderEvent) => {
    const newVisibleOrder = e.columns
      .slice()
      .sort((a, b) => (a.orderIndex ?? 0) - (b.orderIndex ?? 0))
      .map(col => col.field!)
      .filter(Boolean);
    // Hidden columns are not in e.columns; keep them at the end in their current order.
    const hiddenFieldNames = columnOrderRef.current.filter(fn => !newVisibleOrder.includes(fn));
    columnOrderRef.current = [...newVisibleOrder, ...hiddenFieldNames];
    if (props.storageKey) {
      const allOrdered = columnOrderRef.current
        .map(fn => columns.find(c => c.fieldName === fn))
        .filter((c): c is IGridLayout => c !== undefined);
      saveColumnsToStorage(props.storageKey, allOrdered);
    }
    // Do NOT call setColumns: changing React children order while Kendo has
    // already updated its internal _columns via forceUpdate causes readColumns
    // to cross-match old columns by position, scrambling orderIndex values.
  };

  const toggleColumnVisibility = (fieldName: string) => {
    setColumns(prev => prev.map(col =>
      col.fieldName === fieldName ? { ...col, visible: !col.visible } : col
    ));
  };

  const handleColumnResize = (e: GridColumnResizeEvent) => {
    if (!e.end) return;
    setColumns(prev => prev.map(col => {
      const updated = e.columns.find(c => c.field === col.fieldName);
      return updated ? { ...col, colWidth: updated.width as number } : col;
    }));
    // useEffect handles saving in columnOrderRef order
  };

  const excelExport = () => {
    if (_export.current !== null) {
      _export.current.save();
    }
  };

  const addNew = () => {
    if (props.handleAddNew) {
      props.handleAddNew(undefined);
    }
  };

  const remove = (dataItem: any) => {
    if (props.handleDelete) {
      props.handleDelete(dataItem);
    }
  };

  const commandCell = (cell: GridCellProps) => {
    return ( <td className="k-command-cell">
        <AsolviButton buttonText={""} buttonType={"button"} buttonIconName={"delete"} onClick={() => remove(cell.dataItem)} />
      </td>
    );
  };

  const visibleColumns = columns.filter(column => column.visible)
  const columnElements = visibleColumns.map((column, key) => (
    <ExcelExportColumn
      field={column.fieldName}
      title={column.fieldLanguageKey || column.fieldName}
      key={key}
    />
  ));

  const initialSort: Array<SortDescriptor> = [{
    field: props.gridLayout
      ? props.gridLayout[0].fieldName
      : "" ,
    dir: props.gridLayout
      ? (props.gridLayout[0].initialSort
        ? props.gridLayout[0].initialSort
        : "desc")
      : undefined
  }];

  const [sort, setSort] = React.useState(initialSort);

  return (
    <Container fluid className="AsolviGrid">
      <Row>
        <ExcelExport
          data={filterState
            ? orderBy(filterBy(props.data, filterState), sort)
            : orderBy(props.data, sort)} ref={_export}
        >
          {columnElements}
        </ExcelExport>
        <Col sm={12}>
          <Grid
            style={gridStyle}
            sortable={props.sortable ?? false}
            filterable={props.filterable ?? false}
            resizable={props.resizable ?? false}
            reorderable={props.reorderable ?? false}
            onFilterChange={(event: GridFilterChangeEvent) => setFilterState(event.filter)}
            filter={filterState}
            data={filterState ? orderBy(filterBy(props.data, filterState), sort): orderBy(props.data, sort)}
            dataItemKey={props.dataKeyField}
            onDataStateChange={props.handleChange}
            editField="inEdit"
            onRowClick={props.handleRowClick}
            onItemChange={props.handleItemChange}
            sort={sort}
            onSortChange={(e: GridSortChangeEvent) => {
              setSort(e.sort);
            }}
            onColumnReorder={handleColumnReorder}
            onColumnResize={handleColumnResize}
          >
            {(props.enableExcelExport || props.enableAddNew || props.enableColumnChooser) &&
              <GridToolbar>
                <Row>
                  {props.enableAddNew && (<Col sm={4}>
                    <AsolviButton
                      buttonText={t("AsolviGrid.AddNewButton")}
                      buttonIconName={"plus"}
                      onClick={addNew}
                      buttonType={"button"}
                    />
                  </Col>)
                  }
                  {props.enableExcelExport && (<Col sm={2}>
                    <AsolviButton
                      buttonText={t("AsolviGrid.ExportExcelButton")}
                      className="k-button k-primary"
                      onClick={excelExport}
                      buttonType={"button"}
                    />
                  </Col>)
                  }
                  {props.enableColumnChooser && (
                    <Col sm="auto">
                      <div ref={chooserRef} className="asolvi-column-chooser-container">
                        <AsolviButton
                          buttonText={t("AsolviGrid.ColumnsButton")}
                          buttonIconName={"columns"}
                          onClick={() => setShowColumnChooser(v => !v)}
                          buttonType={"button"}
                        />
                        {showColumnChooser && (
                          <div className="asolvi-column-chooser-panel">
                            {columns.map(col => (
                              <label key={col.fieldName} className="asolvi-column-chooser-item">
                                <input
                                  type="checkbox"
                                  checked={col.visible}
                                  onChange={() => toggleColumnVisibility(col.fieldName)}
                                />
                                <span>{col.fieldLanguageKey || col.fieldName}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </Col>
                  )}
                </Row>

              </GridToolbar>}

            {
              columns.length > 0 && (columns.map((layout) => layout.visible && (
                <Column
                  field={layout.fieldName}
                  title={layout.fieldLanguageKey || layout.fieldName}
                  editable={layout.editable}
                  width={layout.colWidth + "px" ?? "auto"}
                  editor={layout.editor ?? undefined}
                  format={layout.format ?? undefined}
                  key={layout.fieldName}
                  filter={layout.filterType}
                  cell={layout.cellStyle}
                />
              )))
            }
            { props.enableDelete && (
              <Column cell={commandCell} width="75px" />
            )
            }
          </Grid>
        </Col>
      </Row>
    </Container>
  );
}

export default ReactI18Next.withTranslation()(AsolviGrid);